import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Collapse,
  IconButton,
  CircularProgress,
  LinearProgress,
  Tooltip,
  Alert,
} from '@mui/material';
import {
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  CheckCircleOutline as GoodIcon,
  WarningAmber as WarnIcon,
  ErrorOutline as BadIcon,
  DataObject as TypeIcon,
  Numbers as NumericIcon,
  TextFields as TextIcon,
  CalendarMonth as DateIcon,
  ToggleOn as BooleanIcon,
  AlternateEmail as EmailIcon,
  Key as IdentifierIcon,
} from '@mui/icons-material';
import branding from '../../styles/branding';
import { useErrorToast } from '../common';
import './DataAnalyzerView.css';

const QUALITY_THRESHOLDS = { good: 95, warn: 70 };

function getQualityColor(completeness) {
  if (completeness >= QUALITY_THRESHOLDS.good) return '#2e7d32';
  if (completeness >= QUALITY_THRESHOLDS.warn) return '#ed6c02';
  return '#d32f2f';
}

function getQualityIcon(completeness) {
  if (completeness >= QUALITY_THRESHOLDS.good) return <GoodIcon sx={{ fontSize: 18, color: '#2e7d32' }} />;
  if (completeness >= QUALITY_THRESHOLDS.warn) return <WarnIcon sx={{ fontSize: 18, color: '#ed6c02' }} />;
  return <BadIcon sx={{ fontSize: 18, color: '#d32f2f' }} />;
}

function getQualityLabel(completeness) {
  if (completeness >= QUALITY_THRESHOLDS.good) return 'Good';
  if (completeness >= QUALITY_THRESHOLDS.warn) return 'Fair';
  return 'Poor';
}

const TYPE_CONFIG = {
  numeric:     { label: 'Numeric',     icon: NumericIcon },
  text:        { label: 'Text',        icon: TextIcon },
  date:        { label: 'Date',        icon: DateIcon },
  boolean:     { label: 'Boolean',     icon: BooleanIcon },
  email:       { label: 'Email',       icon: EmailIcon },
  identifier:  { label: 'Identifier',  icon: IdentifierIcon },
};

function getTypeConfig(col) {
  const key = (col.type || '').toLowerCase();
  return TYPE_CONFIG[key] || TYPE_CONFIG.text;
}

function CompletenessBar({ missingPercent, missingCount }) {
  const completePct = 100 - missingPercent;
  const color = getQualityColor(completePct);
  return (
    <Tooltip title={`${missingCount.toLocaleString()} missing (${missingPercent}%)`}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 140 }}>
        <LinearProgress
          variant="determinate"
          value={completePct}
          sx={{
            flex: 1,
            height: 8,
            borderRadius: 4,
            backgroundColor: '#eee',
            '& .MuiLinearProgress-bar': { backgroundColor: color, borderRadius: 4 },
          }}
        />
        <Typography sx={{ fontSize: '11px', fontFamily: branding.fonts.primary, color: '#666', minWidth: 55, textAlign: 'right' }}>
          {missingCount > 0 ? `${missingCount.toLocaleString()} missing` : 'Clean'}
        </Typography>
      </Box>
    </Tooltip>
  );
}

function FrequencyBar({ count, maxCount }) {
  const pct = maxCount > 0 ? (count / maxCount) * 100 : 0;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
      <Box sx={{ flex: 1, height: 6, borderRadius: 3, backgroundColor: '#eee', overflow: 'hidden' }}>
        <Box sx={{ width: `${pct}%`, height: '100%', backgroundColor: branding.colors.primary, borderRadius: 3 }} />
      </Box>
      <Typography sx={{ fontSize: '11px', fontFamily: branding.fonts.primary, color: '#888', minWidth: 32, textAlign: 'right' }}>
        {count.toLocaleString()}
      </Typography>
    </Box>
  );
}

function ColumnRow({ col }) {
  const [expanded, setExpanded] = useState(false);
  const missingCount = col.missingCount ?? 0;
  const missingPercent = col.missingPercent ?? 0;
  const completeness = 100 - missingPercent;

  return (
    <>
      <TableRow
        hover
        onClick={() => setExpanded(!expanded)}
        sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#f5f8fa' } }}
      >
        <TableCell sx={{ width: 40, px: 0.5 }}>
          <IconButton size="small">
            {expanded ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontFamily: branding.fonts.primary, fontWeight: 600, fontSize: '12px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {getQualityIcon(completeness)}
            {col.name}
          </Box>
        </TableCell>
        <TableCell sx={{ fontFamily: branding.fonts.primary, fontSize: '12px' }}>
          {(() => {
            const tc = getTypeConfig(col);
            const Icon = tc.icon;
            return (
              <Chip
                icon={<Icon sx={{ fontSize: 14 }} />}
                label={tc.label}
                size="small"
                variant="outlined"
                sx={{ fontFamily: branding.fonts.primary, fontSize: '11px', height: 24 }}
              />
            );
          })()}
        </TableCell>
        <TableCell sx={{ fontFamily: branding.fonts.primary, fontSize: '12px' }}>
          <CompletenessBar missingPercent={missingPercent} missingCount={missingCount} />
        </TableCell>
        <TableCell sx={{ fontFamily: branding.fonts.primary, fontSize: '12px', textAlign: 'center' }}>
          {col.distinctCount.toLocaleString()}
        </TableCell>
        <TableCell sx={{ fontFamily: branding.fonts.primary, fontSize: '12px', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {col.topValues?.[0]?.value || '—'}
        </TableCell>
      </TableRow>

      {/* Expanded details */}
      <TableRow>
        <TableCell colSpan={6} sx={{ py: 0, borderBottom: expanded ? undefined : 'none' }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box className="analyzer-expanded-row">
              {/* Detailed stats grid */}
              <Box className="analyzer-detail-grid">
                <DetailCard
                  label="Missing"
                  value={`${missingCount.toLocaleString()} (${missingPercent}%)`}
                  color={getQualityColor(completeness)}
                />
                <DetailCard label="Distinct Values" value={col.distinctCount.toLocaleString()} />
                {col.isNumeric && (
                  <>
                    <DetailCard label="Min" value={col.min != null ? col.min.toLocaleString() : '—'} />
                    <DetailCard label="Max" value={col.max != null ? col.max.toLocaleString() : '—'} />
                    <DetailCard label="Avg" value={col.mean != null ? col.mean.toLocaleString(undefined, { maximumFractionDigits: 2 }) : '—'} />
                    <DetailCard label="Std Dev" value={col.stdDev != null ? col.stdDev.toLocaleString(undefined, { maximumFractionDigits: 2 }) : '—'} />
                  </>
                )}
              </Box>

              {/* Top values with frequency bars */}
              {col.topValues?.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography sx={{ fontFamily: branding.fonts.primary, fontWeight: 600, fontSize: '12px', mb: 1, color: '#555' }}>
                    Top {col.topValues.length} Most Frequent Values
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {col.topValues.map((tv, i) => (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontFamily: branding.fonts.primary, fontSize: '11px', minWidth: 120, color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {tv.value || '(empty)'}
                        </Typography>
                        <FrequencyBar count={tv.count} maxCount={col.topValues[0]?.count || 1} />
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Unique values as chips */}
              {col.uniqueValues?.length > 0 && (
                <Box>
                  <Typography sx={{ fontFamily: branding.fonts.primary, fontWeight: 600, fontSize: '12px', mb: 1, color: '#555' }}>
                    Unique Values
                    {col.distinctCount > col.uniqueValues.length && (
                      <span style={{ fontWeight: 400, color: '#999' }}> (showing {col.uniqueValues.length} of {col.distinctCount.toLocaleString()})</span>
                    )}
                  </Typography>
                  <Box className="analyzer-chips-container">
                    {col.uniqueValues.map((val, i) => (
                      <Chip
                        key={i}
                        label={val}
                        size="small"
                        variant="outlined"
                        sx={{
                          fontFamily: branding.fonts.primary,
                          fontSize: '11px',
                          height: 24,
                          borderColor: '#ccc',
                          '&:hover': { backgroundColor: '#f0f0f0' },
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

function DetailCard({ label, value, color }) {
  return (
    <Box className="analyzer-detail-card">
      <Typography sx={{ fontFamily: branding.fonts.primary, fontSize: '10px', color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {label}
      </Typography>
      <Typography sx={{ fontFamily: branding.fonts.primary, fontSize: '14px', fontWeight: 600, color: color || '#333', wordBreak: 'break-word' }}>
        {value}
      </Typography>
    </Box>
  );
}

function HealthCard({ label, value, icon, color }) {
  return (
    <Paper elevation={1} className="analyzer-health-card">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        {icon}
        <Typography sx={{ fontFamily: branding.fonts.primary, fontSize: '11px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          {label}
        </Typography>
      </Box>
      <Typography sx={{ fontFamily: branding.fonts.primary, fontSize: '22px', fontWeight: 700, color: color || branding.colors.primary }}>
        {value}
      </Typography>
    </Paper>
  );
}

const DataAnalyzerView = ({ data, loading, error }) => {
  const { showErrorToast } = useErrorToast();

  useEffect(() => {
    if (error) {
      showErrorToast(error);
    }
  }, [error, showErrorToast]);
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, gap: 2 }}>
        <CircularProgress size={40} sx={{ color: branding.colors.primary }} />
        <Typography sx={{ fontFamily: branding.fonts.primary, color: '#666' }}>
          Analyzing data quality...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return null;
  }

  if (!data) {
    return <Alert severity="info" sx={{ mb: 2 }}>Switch to Data Analyzer to profile your data.</Alert>;
  }

  const { datasetStats, columns } = data;
  const completenessColor = getQualityColor(datasetStats.completenessScore);

  return (
    <Box className="analyzer-container">
      {/* Dataset Health Summary */}
      <Box className="analyzer-health-row">
        <HealthCard
          label="Completeness"
          value={
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
              {datasetStats.completenessScore}%
              <Chip
                label={getQualityLabel(datasetStats.completenessScore)}
                size="small"
                sx={{
                  fontFamily: branding.fonts.primary,
                  fontSize: '10px',
                  height: 20,
                  backgroundColor: completenessColor,
                  color: '#fff',
                  fontWeight: 600,
                }}
              />
            </Box>
          }
          icon={getQualityIcon(datasetStats.completenessScore)}
          color={completenessColor}
        />
        <HealthCard
          label="Total Rows"
          value={datasetStats.totalRows.toLocaleString()}
          icon={<NumericIcon sx={{ fontSize: 16, color: '#888' }} />}
        />
        <HealthCard
          label="Total Columns"
          value={datasetStats.totalColumns.toLocaleString()}
          icon={<TypeIcon sx={{ fontSize: 16, color: '#888' }} />}
        />
        <HealthCard
          label="Duplicate Rows"
          value={datasetStats.duplicateRows.toLocaleString()}
          icon={datasetStats.duplicateRows > 0 ? <WarnIcon sx={{ fontSize: 16, color: '#ed6c02' }} /> : <GoodIcon sx={{ fontSize: 16, color: '#2e7d32' }} />}
          color={datasetStats.duplicateRows > 0 ? '#ed6c02' : '#2e7d32'}
        />
      </Box>

      {/* Column Profiling Table */}
      <Paper
        sx={{
          borderRadius: '16px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          border: '1px solid #e0e0e0',
          overflow: 'hidden',
          mb: 1.5,
        }}
      >
        <TableContainer sx={{ maxHeight: '60vh', overflow: 'auto' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx} style={{ width: 40 }} />
                <TableCell sx={headerCellSx}>Column Name</TableCell>
                <TableCell sx={headerCellSx}>Type</TableCell>
                <TableCell sx={headerCellSx}>Completeness</TableCell>
                <TableCell sx={{ ...headerCellSx, textAlign: 'center' }}>Distinct</TableCell>
                <TableCell sx={headerCellSx}>Most Frequent</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {columns.map((col, idx) => (
                <ColumnRow key={idx} col={col} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

const headerCellSx = {
  fontWeight: 'bold',
  backgroundColor: '#DCEFF2',
  color: branding.colors.primary,
  fontFamily: branding.fonts.primary,
  fontSize: '12px',
  py: 1,
  px: 1.5,
  borderRight: '1px solid #e0e0e0',
};

export default DataAnalyzerView;
